import React, { useEffect, useState } from 'react'
import { createBrowserRouter, RouterProvider, Navigate, useLocation, useParams } from 'react-router-dom'
import { useSession } from '@/features/auth/context/session.context'
import { authFetchJson } from '@/shared/lib/auth-fetch'

// Layouts
import DashboardLayout from './app/(dashboard)/layout'
import RegionLayout from './app/(dashboard)/[region]/layout'

// Pages - Auth
import HomePage from './app/page'
import EmailRegistrationPage from './app/(auth)/email-registration/page'
import ForgotPasswordPage from './app/(auth)/forgot-password/page'
import OtpLoginPage from './app/(auth)/otp-login/page'
import ResetPasswordPage from './app/(auth)/reset-password/page'
import RoleSelectionPage from './app/(auth)/role/page'
import SuccessfullyCreatedPage from './app/(auth)/successfully-created/page'
import SuccessfullySentPage from './app/(auth)/successfully-sent/page'
import SupporterRegistrationPage from './app/(auth)/supporter-registration/page'
import SupporterRegisterPage from './app/(auth)/supporter-registration/register/page'
import SupporterVolunteerPage from './app/(auth)/supporter-registration/volunteer/page'
import VerificationPage from './app/(auth)/verification/page'
import CallbackPage from './app/(auth)/auth/callback/page'
import CompleteOauthPage from './app/(auth)/auth/complete-oauth/page'
import PrivacyPage from './app/privacy/page'

// Pages - Dashboard
import MessagesPage from './app/(dashboard)/messages/page'
import ProfilePage from './app/(dashboard)/profile/page'
import SettingsPage from './app/(dashboard)/settings/page'

// Pages - Region Specific
import HomeownersPage from './app/(dashboard)/[region]/homeowners/page'
import HomeownerDetailPage from './app/(dashboard)/[region]/homeowners/[homeowner-id]/page'
import HomeownerCreatePage from './app/(dashboard)/[region]/homeowners/create/page'
import WorkOrdersPage from './app/(dashboard)/[region]/work-orders/page'
import WorkOrderDetailPage from './app/(dashboard)/[region]/work-orders/[id]/page'
import WorkOrderCreatePage from './app/(dashboard)/[region]/work-orders/create/page'
import WorkOrderEditPage from './app/(dashboard)/[region]/work-orders/edit/[id]/page'
import ReportsPage from './app/(dashboard)/[region]/reports/page'
import VolunteersPage from './app/(dashboard)/[region]/volunteers/page'
import GroupDetailPage from './app/(dashboard)/[region]/volunteers/groups/[groupId]/page'
import BasePage from './app/(dashboard)/[region]/base/page'
import PartnersPage from './app/(dashboard)/[region]/partners/page'
import PartnerCreatePage from './app/(dashboard)/[region]/partners/create/page'
import PartnerEditPage from './app/(dashboard)/[region]/partners/[id]/edit/page'
import AdminPage from './app/(dashboard)/[region]/admin/page'

const DEFAULT_REGION_SLUG = "swannanoa-valley"

// Fallback redirect for paths accessed without region parameter (e.g. /homeowners)
export function RegionRedirect() {
  const { session, profile, isLoading } = useSession()
  const location = useLocation()
  const [resolvedSlug, setResolvedSlug] = useState<string | null>(null)
  const [isResolving, setIsResolving] = useState(true)

  useEffect(() => {
    if (isLoading) return

    if (!session) {
      setIsResolving(false)
      return
    }

    const fetchSlug = async () => {
      try {
        const selectedRegionId = (profile as any)?.selectedRegion ?? (profile as any)?.data?.selectedRegion
        if (selectedRegionId) {
          const res = await authFetchJson<any>(`/regions/${selectedRegionId}`)
          if (res.success && res.data?.slug) {
            setResolvedSlug(res.data.slug)
            return
          }
        }

        // Fallback: fetch active regions and use the first one
        const regionsRes = await authFetchJson<any>('/regions/options?activeOnly=true')
        if (regionsRes.success) {
          const regions = (regionsRes.data as any)?.data ?? regionsRes.data
          if (Array.isArray(regions) && regions.length > 0) {
            setResolvedSlug(regions[0].slug)
            return
          }
        }
        setResolvedSlug(DEFAULT_REGION_SLUG)
      } catch {
        setResolvedSlug(DEFAULT_REGION_SLUG)
      } finally {
        setIsResolving(false)
      }
    }

    fetchSlug()
  }, [isLoading, session, profile])

  if (isLoading || isResolving) {
    return (
      <div className="flex h-screen w-screen items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    )
  }

  if (!session) {
    return <Navigate to="/login" replace />
  }

  const segments = location.pathname.split('/').filter(Boolean)
  const targetPath = `/${resolvedSlug || DEFAULT_REGION_SLUG}/${segments.join('/')}${location.search}`
  return <Navigate to={targetPath} replace />
}

export function ToolsSuppliesRedirect() {
  const { region } = useParams()
  return <Navigate to={`/${region || DEFAULT_REGION_SLUG}/admin?tab=tools-supplies`} replace />
}

export function MigrationRedirect() {
  const { region } = useParams()
  return <Navigate to={`/${region || DEFAULT_REGION_SLUG}/admin?tab=migration`} replace />
}

const router = createBrowserRouter([
  // Public Routes
  {
    path: '/',
    element: <HomePage />,
  },
  {
    path: '/login',
    element: <HomePage />,
  },
  {
    path: '/email-registration',
    element: <EmailRegistrationPage />,
  },
  {
    path: '/forgot-password',
    element: <ForgotPasswordPage />,
  },
  {
    path: '/otp-login',
    element: <OtpLoginPage />,
  },
  {
    path: '/reset-password',
    element: <ResetPasswordPage />,
  },
  {
    path: '/role',
    element: <RoleSelectionPage />,
  },
  {
    path: '/successfully-created',
    element: <SuccessfullyCreatedPage />,
  },
  {
    path: '/successfully-sent',
    element: <SuccessfullySentPage />,
  },
  {
    path: '/supporter-registration',
    element: <SupporterRegistrationPage />,
  },
  {
    path: '/supporter-registration/register',
    element: <SupporterRegisterPage />,
  },
  {
    path: '/supporter-registration/volunteer',
    element: <SupporterVolunteerPage />,
  },
  {
    path: '/verification',
    element: <VerificationPage />,
  },
  {
    path: '/privacy',
    element: <PrivacyPage />,
  },
  {
    path: '/auth/callback',
    element: <CallbackPage />,
  },
  {
    path: '/auth/complete-oauth',
    element: <CompleteOauthPage />,
  },

  // Protected Dashboard Routes
  {
    element: <DashboardLayout />,
    children: [
      {
        path: '/messages',
        element: <MessagesPage />,
      },
      {
        path: '/profile',
        element: <ProfilePage />,
      },
      {
        path: '/settings',
        element: <SettingsPage />,
      },
      
      // Region-Aware Routes (Wrapped in RegionLayout)
      {
        path: '/:region',
        element: <RegionLayout />,
        children: [
          {
            path: 'homeowners',
            element: <HomeownersPage />,
          },
          {
            path: 'homeowners/create',
            element: <HomeownerCreatePage />,
          },
          {
            path: 'homeowners/:homeownerId',
            element: <HomeownerDetailPage />,
          },
          {
            path: 'work-orders',
            element: <WorkOrdersPage />,
          },
          {
            path: 'work-orders/create',
            element: <WorkOrderCreatePage />,
          },
          {
            path: 'work-orders/:id',
            element: <WorkOrderDetailPage />,
          },
          {
            path: 'work-orders/edit/:id',
            element: <WorkOrderEditPage />,
          },
          {
            path: 'reports',
            element: <ReportsPage />,
          },
          {
            path: 'volunteers',
            element: <VolunteersPage />,
          },
          {
            path: 'volunteers/groups/:groupId',
            element: <GroupDetailPage />,
          },
          {
            path: 'base',
            element: <BasePage />,
          },
          {
            path: 'partners',
            element: <PartnersPage />,
          },
          {
            path: 'partners/create',
            element: <PartnerCreatePage />,
          },
          {
            path: 'partners/:id/edit',
            element: <PartnerEditPage />,
          },
          {
            path: 'admin',
            element: <AdminPage />,
          },
          {
            path: 'tools-supplies',
            element: <ToolsSuppliesRedirect />,
          },
          {
            path: 'migration',
            element: <MigrationRedirect />,
          },
        ]
      },

      // Fallbacks to trigger region resolution redirects
      {
        path: '/homeowners/*',
        element: <RegionRedirect />,
      },
      {
        path: '/work-orders/*',
        element: <RegionRedirect />,
      },
      {
        path: '/reports/*',
        element: <RegionRedirect />,
      },
      {
        path: '/volunteers/*',
        element: <RegionRedirect />,
      },
      {
        path: '/base/*',
        element: <RegionRedirect />,
      },
      {
        path: '/partners/*',
        element: <RegionRedirect />,
      },
      {
        path: '/admin/*',
        element: <RegionRedirect />,
      },
      {
        path: '/tools-supplies/*',
        element: <RegionRedirect />,
      },
    ]
  },

  // Catch-all route -> redirect to login
  {
    path: '*',
    element: <Navigate to="/login" replace />,
  }
])

export default function App() {
  return <RouterProvider router={router} />
}
